import { useEffect, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useLoad, act } from "@/lib/hooks";
import { api } from "@/lib/api";
import { useAccess, useAuth } from "@/context/AuthContext";
import { COUNTRY_NAME } from "@/lib/countries";
import { PageHeader, Section, Field, NativeSelect } from "@/components/common";
import { CompanyProfile } from "./CompanyProfile";
import { AccountDetails } from "./AccountDetails";
import { SizeUomSetting } from "./SizeUomSetting";
import { StorageUsage } from "./StorageUsage";
import { PortalSettings } from "./PortalSettings";
import { CrmSettings } from "./CrmSettings";
import { NotificationSettings } from "./NotificationSettings";
import { DocumentSettings } from "./DocumentSettings";
import { AgyinProSettings } from "./AgyinProSettings";

const KINDS = [{ value: "vat", label: "VAT" }, { value: "levy", label: "Levy" }, { value: "withholding", label: "Withholding" }];
const blankRule = { code: "", name: "", kind: "levy", rate: 0, sales_account: "2100", purchase_account: "1300", default: false, active: true };

function TaxRules({ rules, setRules, can, onSave }) {
  const set = (i, k, v) => setRules(rules.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <Section title="Tax rules" testId="tax-rules-section" action={can && <Button size="sm" variant="outline" data-testid="tax-add-button" onClick={() => setRules([...rules, { ...blankRule }])}><Plus className="mr-1 h-4 w-4" />Add tax</Button>}>
      <div className="overflow-x-auto"><table className="po-table">
        <thead><tr><th>Code</th><th>Name</th><th>Type</th><th className="po-num">Rate %</th><th>Sales GL</th><th>Purchase GL</th><th>Default on lines</th><th>Active</th><th></th></tr></thead>
        <tbody>{rules.map((r, i) => (
          <tr key={i} data-testid={`tax-row-${i}`}>
            <td><Input className="w-24" data-testid={`tax-code-${i}`} disabled={!can} value={r.code} onChange={(e) => set(i, "code", e.target.value.toUpperCase())} /></td>
            <td><Input className="min-w-[180px]" data-testid={`tax-name-${i}`} disabled={!can} value={r.name} onChange={(e) => set(i, "name", e.target.value)} /></td>
            <td><NativeSelect testId={`tax-kind-${i}`} value={r.kind} onChange={(v) => set(i, "kind", v)} options={KINDS} /></td>
            <td><Input className="w-20 text-right" type="number" step="0.01" data-testid={`tax-rate-${i}`} disabled={!can} value={r.rate} onChange={(e) => set(i, "rate", e.target.value)} /></td>
            <td><Input className="w-20" data-testid={`tax-sales-acct-${i}`} disabled={!can} value={r.sales_account} onChange={(e) => set(i, "sales_account", e.target.value)} /></td>
            <td><Input className="w-20" data-testid={`tax-purchase-acct-${i}`} disabled={!can} value={r.purchase_account} onChange={(e) => set(i, "purchase_account", e.target.value)} /></td>
            <td><label className="inline-flex min-h-[40px] min-w-[44px] cursor-pointer items-center"><Switch data-testid={`tax-default-${i}`} disabled={!can} checked={r.default} onCheckedChange={(v) => set(i, "default", v)} /></label></td>
            <td><label className="inline-flex min-h-[40px] min-w-[44px] cursor-pointer items-center"><Switch data-testid={`tax-active-${i}`} disabled={!can} checked={r.active} onCheckedChange={(v) => set(i, "active", v)} /></label></td>
            <td>{can && <button data-testid={`tax-remove-${i}`} className="text-slate-400 hover:text-rose-600" onClick={() => setRules(rules.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>}</td>
          </tr>))}</tbody>
      </table></div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3">
        <p className="text-xs text-slate-500">Every tax is calculated on the line amount before tax (no compounding). Taxes marked "Default on lines" apply to new invoice and POS lines automatically; you can change them on each line. Withholding tax reduces the amount the customer owes.</p>
        {can && <Button data-testid="tax-save-button" onClick={onSave} className="bg-[#13294B] hover:bg-[#1c3a66]">Save tax rules</Button>}
      </div>
    </Section>
  );
}

export default function Settings() {
  const [d, reload] = useLoad("/tenant/settings");
  const [p, setP] = useState(null);
  const [rules, setRules] = useState(null);
  const [target, setTarget] = useState("");
  const { canWrite } = useAccess();
  const portalOn = useAuth().user?.tenant?.capabilities?.includes("client_portal");
  const proOn = useAuth().user?.tenant?.capabilities?.includes("ai_pro");
  const can = canWrite(["tenant_admin"]);

  useEffect(() => {
    if (!d) return;
    setP({ milestone_enforcement: d.payment_settings.milestone_enforcement ?? true, min_partial_amount: d.payment_settings.min_partial_amount ?? "" });
    setRules(d.tax_rules);
    setTarget(d.targets?.revenue_monthly ?? "");
  }, [d]);
  if (!d || !p || !rules) return null;

  const saveP = async () => {
    const body = { milestone_enforcement: p.milestone_enforcement, min_partial_amount: p.min_partial_amount === "" ? null : Number(p.min_partial_amount) };
    if (await act(() => api.put("/tenant/settings/payments", body), "Payment rules saved")) reload();
  };
  const saveT = async () => {
    if (await act(() => api.put("/tenant/settings/taxes", { rules: rules.map((r) => ({ ...r, rate: Number(r.rate) })) }), "Tax rules saved")) reload();
  };
  const saveTarget = async () => {
    if (await act(() => api.put("/tenant/settings/targets", { revenue_monthly: target === "" ? null : Number(target) }), "Revenue target saved")) reload();
  };
  const saveProofing = async (v) => {
    if (await act(() => api.put("/tenant/settings/proofing", { required: v }), v ? "New jobs need customer proofing" : "New jobs skip customer proofing")) reload();
  };
  return (
    <>
      <PageHeader title="Settings" subtitle={`${d.name} · ${COUNTRY_NAME[d.country] || "No country set"} · base currency ${d.base_currency}`}>
        <Button asChild variant="outline" data-testid="settings-data-import-link"><Link to="/app/settings/import"><Upload className="mr-1 h-4 w-4" />Data import</Link></Button>
      </PageHeader>
      <TaxRules rules={rules} setRules={setRules} can={can} onSave={saveT} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <Section title="Targets" testId="targets-section">
          <div className="space-y-3 p-5">
            <Field label={`Monthly revenue target (${d.base_currency})`}><Input type="number" step="0.01" data-testid="revenue-target-input" disabled={!can} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 50000" /></Field>
            <p className="text-xs text-slate-500">Shown as revenue vs target on the executive dashboard.</p>
            {can && <Button data-testid="revenue-target-save-button" onClick={saveTarget} className="bg-[#13294B] hover:bg-[#1c3a66]">Save target</Button>}
          </div>
        </Section>
        <AccountDetails />
        <SizeUomSetting canEdit={can} />
        <CompanyProfile d={d} can={can} onSaved={reload} />
        <div className="self-start"><StorageUsage storage={d.storage} /></div>
        {portalOn && <Section title="Customer proofing" testId="proofing-settings-section">
          <div className="space-y-3 p-5">
            <div className="flex items-center justify-between gap-4"><div><div className="text-sm font-medium">Jobs need customer proofing</div>
              <div className="text-xs text-slate-500">Default for new jobs. Job types can override it, and each job can be changed on its job card. Walk-in and counter jobs start as not needed. Existing jobs are not changed.</div></div>
              <Switch data-testid="proofing-default-switch" checked={d.proofing_required} disabled={!can} onCheckedChange={saveProofing} /></div>
          </div>
        </Section>}
        {portalOn && <PortalSettings d={d} can={can} onSaved={reload} />}
        <CrmSettings can={can} />
        <DocumentSettings can={can} />
        <NotificationSettings can={can} />
        {can && <AgyinProSettings pro={proOn} />}
        <Section title="Milestone payment rules">
          <div className="space-y-4 p-5">
            <div className="flex items-center justify-between gap-4"><div><div className="text-sm font-medium">Show milestone minimums</div><div className="text-xs text-slate-500">When a payment template is attached, the invoice shows the milestone due.</div></div>
              <Switch data-testid="milestone-enforcement-switch" checked={p.milestone_enforcement} disabled={!can} onCheckedChange={(v) => setP({ ...p, milestone_enforcement: v })} /></div>
            <Field label="Minimum partial payment (no template)"><Input type="number" step="0.01" data-testid="min-partial-input" disabled={!can} value={p.min_partial_amount} onChange={(e) => setP({ ...p, min_partial_amount: e.target.value })} placeholder="Leave blank for no minimum" /></Field>
            <p className="text-xs text-slate-500">Payments are recorded manually by your team. Overpayments are always rejected.</p>
            {can && <Button data-testid="payment-rules-save-button" onClick={saveP} className="bg-[#13294B] hover:bg-[#1c3a66]">Save payment rules</Button>}
          </div>
        </Section>
      </div>
    </>
  );
}
